import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  enterMeet,
  fetchMeetView,
  MeetError,
  MeetSession,
  MeetView,
} from "../../hooks/api/meet";
import { CallWorkspace } from "./CallWorkspace";
import { formatCountdown, formatSessionDate, sessionPhase } from "./meetTime";

const REFRESH_MS = 5 * 60_000;

type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "unavailable" }
  | { kind: "ready"; view: MeetView };

/**
 * /meet/:token — videollamada de la psicóloga con psi-video, con su panel privado al costado
 * (ver `CallWorkspace`). Va dentro del layout de la plataforma, pero no se enlaza desde ningún
 * otro lugar: solo se llega por su URL (el link fijo de la psicóloga).
 * Con `?session=` viene preseleccionada la sesión de ahora. Si todavía usa Google Meet, la
 * lleva a su link.
 */
export const MeetPage = () => {
  const { token = "" } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const wanted = params.get("session");

  const [state, setState] = useState<State>({ kind: "loading" });
  const [offsetMs, setOffsetMs] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [picked, setPicked] = useState<string | null>(wanted);
  const [call, setCall] = useState<{
    url: string;
    session: MeetSession;
  } | null>(null);
  const [entering, setEntering] = useState(false);
  const [enterError, setEnterError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const view = await fetchMeetView(token);
      setOffsetMs(Date.parse(view.serverTime) - Date.now());
      setState({ kind: "ready", view });
    } catch (e) {
      setState({
        kind:
          e instanceof MeetError && e.kind === "invalid"
            ? "invalid"
            : "unavailable",
      });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() + offsetMs), 1000);
    return () => clearInterval(t);
  }, [offsetMs]);

  // Con Google Meet todavía: se la lleva a su link (queda el botón por si el navegador lo frena)
  const view = state.kind === "ready" ? state.view : null;
  useEffect(() => {
    if (view?.provider === "meet" && view.meetLink)
      window.location.replace(view.meetLink);
  }, [view]);

  // Sesión elegida: la del link, si no la sugerida (en curso o por empezar), si no la primera
  const selected: MeetSession | undefined =
    view?.sessions.find((s) => s.sessionId === picked) ??
    view?.sessions.find((s) => s.sessionId === view.suggestedSessionId) ??
    view?.sessions[0];

  const enter = async (session: MeetSession) => {
    setEntering(true);
    setEnterError(null);
    try {
      const r = await enterMeet(token, session.sessionId);
      if (r.url && r.provider === "psi-video") setCall({ url: r.url, session });
      else if (r.url) window.location.replace(r.url);
      else setEnterError("Todavía no hay una sesión para entrar.");
    } catch {
      setEnterError(
        "No pudimos abrir la sala. Probá de nuevo en unos segundos.",
      );
    } finally {
      setEntering(false);
    }
  };

  if (call) {
    return (
      <CallWorkspace
        url={call.url}
        linkToken={token}
        session={call.session}
        onClose={() => {
          setCall(null);
          void load();
        }}
      />
    );
  }

  return (
    <main className="mx-auto max-w-xl px-4 py-10">
      <h1 className="mb-6 text-xl font-semibold">Tu videollamada</h1>
      {state.kind === "loading" && <p role="status">Cargando…</p>}
      {state.kind === "invalid" && (
        <p>
          Este link no es válido. Pedí uno nuevo al equipo de Psi Mammoliti.
        </p>
      )}
      {state.kind === "unavailable" && (
        <div>
          <p className="mb-3">
            No pudimos conectar con el servicio. Revisá tu conexión.
          </p>
          <button
            className="rounded-lg border px-4 py-2"
            onClick={() => void load()}
          >
            Reintentar
          </button>
        </div>
      )}
      {view?.provider === "meet" && (
        <div>
          <p className="mb-3">Tus sesiones todavía son por Google Meet.</p>
          {view.meetLink ? (
            <a
              className="rounded-lg bg-black px-4 py-2 text-white"
              href={view.meetLink}
            >
              Ir a mi Google Meet
            </a>
          ) : (
            <p>
              No tenés tu link de Meet cargado. Avisá al equipo de Psi
              Mammoliti.
            </p>
          )}
        </div>
      )}
      {view?.provider === "psi-video" && !selected && (
        <p>No tenés sesiones en los próximos 7 días.</p>
      )}
      {view?.provider === "psi-video" && selected && (
        <div className="space-y-6">
          <section className="rounded-2xl border p-5">
            <p className="text-sm text-gray-500">Sesión elegida</p>
            <p className="text-lg font-semibold">
              {formatSessionDate(selected.start)}
            </p>
            <p className="mb-4 text-sm text-gray-600">
              {selected.durationMin} min · {sessionLabel(selected, now)}
            </p>
            <button
              className="rounded-lg bg-black px-5 py-2.5 font-medium text-white disabled:opacity-50"
              disabled={entering}
              onClick={() => void enter(selected)}
            >
              {entering ? "Abriendo…" : "Entrar a la sesión"}
            </button>
            {enterError && (
              <p role="alert" className="mt-3 text-sm text-red-600">
                {enterError}
              </p>
            )}
            <p className="mt-3 text-xs text-gray-500">
              Dentro vas a poder elegir cámara y micrófono antes de entrar.
            </p>
          </section>
          {view.sessions.length > 1 && (
            <section>
              <h2 className="mb-2 text-sm font-medium text-gray-600">
                Próximas sesiones
              </h2>
              <ul className="divide-y rounded-xl border">
                {view.sessions.map((s) => (
                  <li key={s.sessionId}>
                    <button
                      className={`flex w-full items-center justify-between px-4 py-3 text-left ${
                        s.sessionId === selected.sessionId ? "bg-gray-100" : ""
                      }`}
                      onClick={() => setPicked(s.sessionId)}
                    >
                      <span>{formatSessionDate(s.start)}</span>
                      <span className="text-xs text-gray-500">
                        {sessionLabel(s, now)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </main>
  );
};

function sessionLabel(s: MeetSession, now: number): string {
  const phase = sessionPhase(s.start, s.durationMin, now);
  if (phase.kind === "live") return "En curso";
  if (phase.kind === "over") return "Terminó";
  return `Empieza en ${formatCountdown(phase.ms)}`;
}

export default MeetPage;
